import { Bounds, OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { SRGBColorSpace, Vector3 } from "three";
import ErrorComponent from "./helper/ErrorComponent.jsx";
import LightElements from "./helper/LightElements.jsx";
import { getModelFileType, zoom } from "./ModelPreview.jsx";
import ModelFBX from "./models/ModelFBX.jsx";
import ModelGLTF from "./models/ModelGLTF.jsx";
import ModelOBJ from "./models/ModelOBJ.jsx";
import ModelOBJZip from "./models/ModelOBJZip.jsx";

const ScreenshotPreview = ({ url, data, fileName, contentType, rotation, resolve }) => {

  const ref = {
    meshRef: useRef(),
    controlRef: useRef(),
    sceneRef: useRef()
  }

  const cameraRef = useRef();

  useEffect(() => {
    if (cameraRef.current) {
      cameraRef.current.position.set(...zoom(10.0, new Vector3(1.0, 1.5, 3.0)));
    }
  }, []);

  /**
   * Takes a screenshot of the current model.
   * @param {*} gl 
   */
  const onScreenshot = (gl) => {
    gl.shadowMap.enabled = true;
    // make background white
    gl.setClearColor(0xffffff, 1);
    // create the screenshot with 1 second timeout
    setTimeout(() => {
      // make a blob and resolve the promise
      gl.domElement.toBlob((screenshot => {
        // remove ScreenshotPreview from document
        const container = document.getElementById('tempscreenshot');
        document.body.removeChild(container);
        // resolve screenshot
        resolve(screenshot);
      }), 'image/jpeg');
    }, 500);
  }

  const modelElement = useMemo(() => {
    const fileType = getModelFileType(fileName, contentType);
    if (fileType === 'fbx') {
      return <ModelFBX url={url} data={data} adjustScale={value => null} ref={ref} rotation={rotation} onLoad={value => null} onScreenshot={onScreenshot} />;
    } else if (fileType === 'gltf' || fileType === 'glb') {
      return <ModelGLTF url={url} data={data} adjustScale={value => null} ref={ref} rotation={rotation} onLoad={value => null} onScreenshot={onScreenshot} />;
    } else if (fileType === 'obj') {
      return <ModelOBJ url={url} data={data} adjustScale={value => null} ref={ref} rotation={rotation} onLoad={value => null} onScreenshot={onScreenshot} />;
    } else if (fileType === 'objZip') {
      return <ModelOBJZip url={url} data={data} adjustScale={value => null} ref={ref} rotation={rotation} onLoad={value => null} onScreenshot={onScreenshot} />
    } else {
      return null;
    }
  }, [url, data, fileName, rotation])

  return (
    <ErrorBoundary FallbackComponent={ErrorComponent}>
      <Canvas
        color={0xffffff}
        linear
        gl={{ preserveDrawingBuffer: true, outputColorSpace: SRGBColorSpace }}
      >
        <LightElements />

        {url && (
          <Bounds fit clip observe damping={6} maxDuration={0} margin={1.2} >
            {modelElement}
          </Bounds>
        )}

        <PerspectiveCamera makeDefault ref={cameraRef} onUpdate={(self) => self.position.set(...zoom(10.0, new Vector3(1.0, 1.5, 3.0)))} />
        <OrbitControls makeDefault enablePan={false} ref={ref.controlRef} />
      </Canvas>
    </ErrorBoundary>
  );
};

export default ScreenshotPreview;